import React, { useEffect, useState } from "react";
import { X as XIcon, Sparkles } from "lucide-react";
import api from "../../lib/apiClient";

// XLandingFunnel — two responsibilities, one mount point:
//   1. Anonymous attribution beacon — when the URL has utm_source=x,
//      we fire POST /api/x/attribution/log ONCE per session and
//      persist the {utm_source, utm_campaign, list_slug,
//      attribution_id} tuple into localStorage. The XAuthButton +
//      X callback page pick those up later to credit the welcome
//      bonus to the right tracked list.
//   2. Welcome banner — for the same visit, we surface a soft
//      "Welcome from X — claim your free 50 credits" callout on the
//      landing TV. Dismissible. Self-hides on subsequent visits
//      via a localStorage flag so we don't nag.
//
// Renders nothing by default. Mounted unconditionally on the public
// landing FeedPage; ProfilePage doesn't mount it (we only want one
// welcome banner per session).
const XLandingFunnel = ({ user }) => {
  const [visible, setVisible] = useState(false);
  const [campaign, setCampaign] = useState("");
  // Dynamic bonus copy — backed by GET /api/x/welcome-bonus. Defaults
  // to 50 cr until the call resolves; if early-bird is active, the
  // banner upgrades to "First N — only K left!"
  const [bonusInfo, setBonusInfo] = useState({ bonus: 50, earlybird_active: false, earlybird_remaining: 0 });

  useEffect(() => {
    api.get("/x/welcome-bonus")
      .then((r) => { if (r?.data) setBonusInfo(r.data); })
      .catch(() => { /* fall back to defaults — banner still renders */ });
  }, []);

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const utmSource = sp.get("utm_source") || "";
    const utmCampaign = sp.get("utm_campaign") || "";
    const listSlug = sp.get("list") || "";

    // If this paint includes the UTM params, persist them BEFORE
    // potentially showing the banner — the XAuthButton reads from
    // localStorage so the user can navigate away and come back.
    if (utmSource && /^(x|twitter)$/i.test(utmSource)) {
      try {
        localStorage.setItem("ge.x_utm_source", utmSource);
        if (utmCampaign) localStorage.setItem("ge.x_utm_campaign", utmCampaign);
        if (listSlug) localStorage.setItem("ge.x_list_slug", listSlug);
      } catch (e) { /* private mode — best-effort */ }
      // Fire the attribution beacon, but only once per browser session.
      // The backend logs each call (one row per call), so re-firing on
      // every refresh would pollute reporting.
      const beaconKey = "ge.x_attribution_logged";
      const alreadyLogged = sessionStorage.getItem(beaconKey);
      if (!alreadyLogged) {
        sessionStorage.setItem(beaconKey, "1");
        api.post("/x/attribution/log", {
          utm_source: utmSource,
          utm_campaign: utmCampaign || undefined,
          list_slug: listSlug || undefined,
          referrer: document.referrer || undefined,
          landed_at: new Date().toISOString(),
        }).then((r) => {
          const attributionId = r?.data?.attribution_id || "";
          if (attributionId) {
            try {
              localStorage.setItem("ge.x_attribution_id", attributionId);
            } catch { /* swallow */ }
          }
        }).catch(() => { /* beacon is best-effort */ });
      }
    }

    // Banner visibility decision — show ONLY if:
    //  - User is anonymous (banner is a sign-up CTA)
    //  - We have a fresh X attribution (current paint OR saved within
    //    the last 72h)
    //  - User hasn't dismissed it this session
    const dismissed = sessionStorage.getItem("ge.x_welcome_dismissed");
    if (user || dismissed) return;
    const haveFresh = utmSource && /^(x|twitter)$/i.test(utmSource);
    const stored = (() => {
      try { return localStorage.getItem("ge.x_utm_source") || ""; }
      catch { return ""; }
    })();
    const showBanner = haveFresh || /^(x|twitter)$/i.test(stored);
    if (showBanner) {
      setVisible(true);
      setCampaign(utmCampaign || (() => {
        try { return localStorage.getItem("ge.x_utm_campaign") || ""; }
        catch { return ""; }
      })());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!visible) return null;

  const dismiss = () => {
    try { sessionStorage.setItem("ge.x_welcome_dismissed", "1"); } catch { /* swallow */ }
    setVisible(false);
  };

  return (
    <div
      className="mx-auto max-w-2xl mt-3 mb-1 px-4 py-3 rounded-lg border border-emerald-500/30 bg-gradient-to-r from-black via-emerald-950/40 to-black flex items-center gap-3 text-sm"
      data-testid="x-welcome-banner"
    >
      <div className="shrink-0 h-9 w-9 rounded-full bg-black border border-emerald-500/40 flex items-center justify-center">
        <XIcon className="h-4 w-4 text-emerald-300" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium leading-tight">
          Welcome from X
          {campaign ? <span className="text-emerald-300"> · {campaign}</span> : null}
        </p>
        <p className="text-gray-400 text-xs flex items-center gap-1 mt-0.5">
          <Sparkles className="h-3 w-3 text-emerald-400" />
          {bonusInfo.earlybird_active ? (
            <>
              First {bonusInfo.earlybird_cap || 100} sign-ups: <span className="text-emerald-300 font-medium mx-1">+{bonusInfo.bonus} free credits</span> —{" "}
              <span className="text-amber-300 font-medium">only {bonusInfo.earlybird_remaining} left</span>.
            </>
          ) : (
            <>Sign up with X to claim {bonusInfo.bonus} free credits — instant start, no card needed.</>
          )}
        </p>
      </div>
      <button
        type="button"
        onClick={() => {
          // The Header listens for `open-auth` and pops the
          // AuthForm modal (same dialog Google + Apple + X buttons
          // live inside). Saves us from inventing a route just for
          // this single CTA.
          window.dispatchEvent(new CustomEvent("open-auth"));
        }}
        className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium"
        data-testid="x-welcome-cta"
      >
        Claim free credits →
      </button>
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 text-gray-500 hover:text-white text-xs"
        aria-label="Dismiss"
        data-testid="x-welcome-dismiss"
      >
        ✕
      </button>
    </div>
  );
};

export default XLandingFunnel;
